import { useState, useEffect, useRef } from 'react';
import { StreamChat } from 'stream-chat';
import { getStreamApiKey } from '../api/client';
import type { ChatMessage } from './useLiveChatMessages';

/**
 * Read-only chat hook for unauthenticated users.
 * Connects anonymously to Stream Chat and watches the channel for messages.
 */
export function useReadOnlyChat(channelId: string | null) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const clientRef = useRef<StreamChat | null>(null);

  useEffect(() => {
    if (!channelId) return;

    let cancelled = false;

    async function connect() {
      try {
        const { apiKey } = await getStreamApiKey();
        const client = StreamChat.getInstance(apiKey);
        await client.connectAnonymousUser();

        if (cancelled) { client.disconnectUser(); return; }

        clientRef.current = client;
        const channel = client.channel('livestream', channelId!);
        await channel.watch();

        if (cancelled) return;

        // Load existing messages
        const existing = (channel.state.messages || [])
          .filter(msg => msg.text)
          .map(msg => ({
            id: msg.id,
            senderId: msg.user?.id || null,
            senderName: msg.user?.name || msg.user?.id || 'Anonymous',
            senderAvatar: (msg.user?.image as string) || null,
            content: msg.text!,
            isLocal: false,
          }));
        setMessages(existing);
        setIsConnected(true);

        // Listen for new messages
        channel.on('message.new', (event) => {
          const msg = event.message;
          if (!msg || !msg.text) return;
          const newMsg: ChatMessage = {
            id: msg.id,
            senderId: msg.user?.id || null,
            senderName: msg.user?.name || msg.user?.id || 'Anonymous',
            senderAvatar: (msg.user?.image as string) || null,
            content: msg.text,
            isLocal: false,
          };
          setMessages(prev => {
            if (prev.some(m => m.id === newMsg.id)) return prev;
            return [...prev, newMsg];
          });
        });
      } catch (err) {
        console.error('[ReadOnlyChat] Failed to connect:', err);
      }
    }

    connect();

    return () => {
      cancelled = true;
      if (clientRef.current) {
        clientRef.current.disconnectUser().catch(() => {});
        clientRef.current = null;
      }
    };
  }, [channelId]);

  return { messages, isConnected };
}
